import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postReviewAutomationResponse } from "../api/postReviewAutomationResponse";
import { posthogService, PostHogEvent } from "src/common/services/posthogService";

interface UsePostReviewAutomationResponseProps {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

/**
 * Custom React Query mutation hook to post review automation response.
 *
 * @param props - Optional callbacks for onSuccess and onError
 * @returns Object containing the mutation state, handlers, and status flags
 */
export function usePostReviewAutomationResponse({
  onSuccess,
  onError,
}: UsePostReviewAutomationResponseProps = {}) {
  const queryClient = useQueryClient();

  const postResponseMutation = useMutation({
    mutationFn: ({
      responseId,
      payload,
    }: {
      responseId: string;
      payload: { action: string; draft: string };
    }) => postReviewAutomationResponse(responseId, payload),
    mutationKey: ["postReviewAutomationResponse"],
    onSuccess: (data, variables) => {
      // Track Orbit AI review response action (approve / reject / edit / reply) in PostHog
      posthogService.track(PostHogEvent.ORBIT_AI_REVIEW_RESPONSE_POSTED, {
        response_id: variables?.responseId,
        action: variables?.payload?.action
      });

      queryClient.invalidateQueries({
        queryKey: ["getReviewAutomationDashboard"],
      });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    postResponseMutation,
    postResponse: postResponseMutation.mutate,
    postResponseAsync: postResponseMutation.mutateAsync,
    isLoading: postResponseMutation.isPending,
    isSuccess: postResponseMutation.isSuccess,
    isError: postResponseMutation.isError,
    error: postResponseMutation.error,
  };
}

export default usePostReviewAutomationResponse;
